package com.swetdot.swetgram.ui.chat

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.MessageDeleteScope
import com.swetdot.swetgram.data.MessageRepository
import com.swetdot.swetgram.data.PendingPassReport
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.media.ImageSource
import com.swetdot.swetgram.data.media.UploadPurpose
import com.swetdot.swetgram.data.media.UploadRepository
import com.swetdot.swetgram.data.media.MAX_VOICE_DURATION_MS
import com.swetdot.swetgram.data.media.RecordedVoice
import com.swetdot.swetgram.data.media.VoiceCaptureState
import com.swetdot.swetgram.data.media.VoicePlaybackState
import com.swetdot.swetgram.data.media.VoicePlayer
import com.swetdot.swetgram.data.media.VoiceRecorder
import com.swetdot.swetgram.data.newUuid
import com.swetdot.swetgram.data.realtime.RealtimeClient
import com.swetdot.swetgram.data.realtime.RealtimeSignal
import com.swetdot.swetgram.data.streaks.SquadStreakRepository
import com.swetdot.swetgram.domain.Chat
import com.swetdot.swetgram.domain.ChatId
import com.swetdot.swetgram.domain.SquadStreakDomain
import com.swetdot.swetgram.domain.Message
import com.swetdot.swetgram.domain.MessageId
import com.swetdot.swetgram.domain.MessageState
import com.swetdot.swetgram.domain.UserId
import com.swetdot.swetgram.protocol.dto.RealtimeEventTypes
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.count
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.chat_banner_load_refused
import swetgram.shared.generated.resources.chat_banner_stale_detail
import swetgram.shared.generated.resources.chat_banner_stale_offline
import swetgram.shared.generated.resources.chat_banner_stale_refused
import swetgram.shared.generated.resources.chat_banner_stale_timeout
import swetgram.shared.generated.resources.chat_delete_failed
import swetgram.shared.generated.resources.chat_delete_forbidden
import swetgram.shared.generated.resources.chat_delete_too_old
import swetgram.shared.generated.resources.chat_edit_conflict
import swetgram.shared.generated.resources.chat_edit_failed
import swetgram.shared.generated.resources.chat_edit_forbidden
import swetgram.shared.generated.resources.chat_forward_failed
import swetgram.shared.generated.resources.chat_reaction_failed
import swetgram.shared.generated.resources.chat_image_failed
import swetgram.shared.generated.resources.chat_image_rejected
import swetgram.shared.generated.resources.chat_voice_failed
import swetgram.shared.generated.resources.chat_voice_permission_denied
import swetgram.shared.generated.resources.chat_voice_too_short
import swetgram.shared.generated.resources.chat_offline
import swetgram.shared.generated.resources.chat_outbox_failed
import swetgram.shared.generated.resources.chat_pin_failed
import swetgram.shared.generated.resources.chat_pin_limit
import swetgram.shared.generated.resources.chat_send_forbidden
import swetgram.shared.generated.resources.chat_send_invalid
import swetgram.shared.generated.resources.chat_send_not_found
import swetgram.shared.generated.resources.chat_send_offline
import swetgram.shared.generated.resources.chat_send_rate_limited
import swetgram.shared.generated.resources.chat_send_suspended
import swetgram.shared.generated.resources.chat_send_timeout
import swetgram.shared.generated.resources.chat_send_unknown
import swetgram.shared.generated.resources.chat_timeout
import swetgram.shared.generated.resources.chat_unpin_failed
import swetgram.shared.generated.resources.value_passthrough
import kotlin.time.Clock
import kotlin.time.Instant

data class VoiceComposerState(
    val active: Boolean = false,
    val requestingPermission: Boolean = false,
    val locked: Boolean = false,
    val paused: Boolean = false,
    val elapsedMs: Long = 0,
    val amplitude: Int = 0,
    /** Real microphone amplitudes (0..100), newest sample last. */
    val waveform: List<Int> = emptyList(),
    val uploading: Boolean = false,
    val readyToRetry: Boolean = false,
)

sealed interface ComposerContext {
    data object None : ComposerContext
    data class Reply(val message: Message) : ComposerContext
    data class Edit(val message: Message) : ComposerContext
}

private enum class PendingVoiceGesture {
    None,
    Release,
    Lock,
    Cancel,
}

internal enum class VoiceComposerMode {
    Idle,
    RequestingPermission,
    Holding,
    Locked,
    Paused,
    Uploading,
    Retry,
}

/** Collapses the recorder flags into one deterministic UI state. */
internal fun voiceComposerMode(state: VoiceComposerState): VoiceComposerMode = when {
    state.uploading -> VoiceComposerMode.Uploading
    state.readyToRetry -> VoiceComposerMode.Retry
    state.requestingPermission -> VoiceComposerMode.RequestingPermission
    state.locked && state.paused -> VoiceComposerMode.Paused
    state.locked -> VoiceComposerMode.Locked
    state.active -> VoiceComposerMode.Holding
    else -> VoiceComposerMode.Idle
}

/** Keeps the recent, genuine microphone envelope used by the recording composer. */
internal fun appendLiveWaveform(
    samples: List<Int>,
    amplitude: Int,
    capacity: Int = 48,
): List<Int> {
    if (capacity <= 0) return emptyList()
    val next = amplitude.coerceIn(0, 100)
    return if (samples.size < capacity) {
        samples + next
    } else {
        samples.takeLast(capacity - 1) + next
    }
}

class ChatViewModel(
    private val chatId: ChatId,
    private val me: UserId,
    private val repository: MessageRepository,
    private val realtime: RealtimeClient,
    /**
     * A LOOKUP, not a picker.
     *
     * The Android picker is bound to the Activity that registered it with the result registry, and
     * a ViewModel outlives its Activity -- a rotation, a theme change or a restore after process
     * death builds a new one and hands the container a new picker. Holding the instance across
     * that boundary leaves this view model calling into a destroyed registry, where the pick never
     * returns and the attach button silently stops working with nothing to see in a log.
     *
     * So the picker is fetched when the user actually taps. Returns null on a platform with no
     * chooser wired, in which case the screen hides its attach button rather than offering one
     * that cannot do anything. Defaulted so every existing construction site and test keeps
     * compiling.
     */
    private val images: () -> ImageSource? = { null },
    private val uploads: UploadRepository? = null,
    private val voiceRecorders: () -> VoiceRecorder? = { null },
    private val voicePlayer: VoicePlayer? = null,
    private val squadStreaks: SquadStreakRepository? = null,
) : ViewModel() {

    /**
     * True only when this build can actually pick AND upload.
     *
     * A getter rather than a stored flag: it is the same question [attachImage] asks, and asking
     * it once at construction is what the provider above exists to avoid. Whether a chooser exists
     * at all is a property of the platform, so this does not flicker -- it is only the identity of
     * the picker that changes with the Activity.
     */
    val canAttachImages: Boolean get() = images() != null && uploads != null
    val canRecordVoice: Boolean get() = voiceRecorders() != null && uploads != null

    private val _squadStreak = MutableStateFlow<SquadStreakDomain?>(null)
    val squadStreak: StateFlow<SquadStreakDomain?> = _squadStreak.asStateFlow()
    private val _nudgeInProgress = MutableStateFlow(false)
    val nudgeInProgress: StateFlow<Boolean> = _nudgeInProgress.asStateFlow()
    private val _startStreakInProgress = MutableStateFlow(false)
    val startStreakInProgress: StateFlow<Boolean> = _startStreakInProgress.asStateFlow()

    private val _voiceComposer = MutableStateFlow(VoiceComposerState())
    val voiceComposer: StateFlow<VoiceComposerState> = _voiceComposer.asStateFlow()
    val voicePlayback: StateFlow<VoicePlaybackState> =
        voicePlayer?.state ?: MutableStateFlow(VoicePlaybackState())
    private var activeVoiceRecorder: VoiceRecorder? = null
    private var voiceStartJob: Job? = null
    private var voiceCaptureJob: Job? = null
    private var voiceFinishJob: Job? = null
    private var pendingVoiceGesture = PendingVoiceGesture.None
    private var voiceSession = 0L
    private var finishingVoice = false
    private var pendingVoice: RecordedVoice? = null
    private var pendingVoiceMediaId: String? = null
    private var pendingVoiceReply: Message? = null
    private var pendingVoiceClientId: String? = null
    private var pendingVoiceIdempotencyKey: String? = null
    private val reactionLocks = mutableMapOf<Pair<String, String>, Mutex>()

    override fun onCleared() {
        voiceStartJob?.cancel()
        voiceCaptureJob?.cancel()
        voiceFinishJob?.cancel()
        activeVoiceRecorder?.cancel()
        voicePlayer?.stop()
        super.onCleared()
    }

    val chat: StateFlow<Chat?> = repository.observeChat(chatId)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    /**
     * How far the other participant has read, as far as the SOCKET knows. Null until a receipt
     * arrives.
     *
     * Held here rather than written into the cache, and that is the difference between this feature
     * and a refetch. `cachedMessage.state` is the server's projection of `Message.status`, refreshed
     * wholesale by [refresh]; this is a live overlay laid on top of it, so a receipt costs no SQL
     * write, no query invalidation and no network call -- a tick turns and nothing else moves.
     *
     * It only ever increases; see [observeReadReceipts].
     */
    private val _peerReadUpTo = MutableStateFlow<Instant?>(null)

    /**
     * How many rows of this conversation the screen is observing.
     *
     * GROWS AS THE READER PAGES BACK, and is the reason scroll-back works at all: the cache query
     * is `LIMIT ?`, so cached history beyond the window is invisible however much of it is
     * already on the device. [loadOlder] raises this by a page at a time and fetches from the
     * network only when the cache has nothing older left to reveal.
     */
    private val _window = MutableStateFlow(MessageRepository.TIMELINE_WINDOW)

    /**
     * The cursor for the page BEFORE the oldest one fetched, or null if none has been read yet.
     *
     * Plain fields rather than state: nothing renders them, and making them observable would make
     * a scroll position look like screen state. DECLARED ABOVE `init` on purpose -- `viewModelScope`
     * dispatches on `Main.immediate`, so a coroutine launched from `init` can begin running inside
     * the constructor, and a `= null` initialiser reached afterwards would wipe what it wrote.
     */
    private var historyCursor: String? = null
    private var historyExhausted = false

    @OptIn(ExperimentalCoroutinesApi::class)
    private val timeline: Flow<List<Message>> =
        _window.flatMapLatest { limit -> repository.observeMessages(chatId, limit) }

    /**
     * The timeline, with the live read watermark folded in.
     *
     * Recombining on every [chat] emission is not incidental: the fold is refused until the member
     * list identifies a sole counterpart (see [soleCounterpart]), and on a cold open a receipt can
     * arrive before the conversation has been read over REST. Resolving late means such a frame is
     * held in [_peerReadUpTo] and applied the moment the members land, rather than dropped -- the
     * same race, and the same answer, as the typing indicator's late name resolution.
     */
    val messages: StateFlow<List<Message>> =
        combine(timeline, _peerReadUpTo, chat) { rows, readUpTo, conversation ->
            applyReadWatermark(rows, me, readUpTo, soleCounterpart(conversation, me))
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    /**
     * WHAT the banner is about, not what it SAYS. See [ChatBanner].
     *
     * The sentence is derived below, against the timeline, because "Could not load messages" is a
     * statement about the whole conversation and it is FALSE the moment the cache has answered:
     * `ChatScreen` draws this banner ABOVE the timeline rather than instead of it (deliberately --
     * a failed refresh must not blank what the user already had), so in the ordinary case it sat
     * over a perfectly readable conversation contradicting it. What is actually true then is a
     * statement about FRESHNESS, and only the view model knows which of the two it is.
     */
    private val _banner = MutableStateFlow<ChatBanner?>(null)

    /**
     * The conversation-level banner, as a sentence, or null.
     *
     * Recombined with [messages] rather than computed once at failure time, and that is not
     * incidental: on a cold open the network read can fail BEFORE the cache query has answered, so
     * the honest sentence changes from "could not load" to "may be out of date" a frame later
     * without anything failing again. Recombining is what lets it.
     */
    val error: StateFlow<UiText?> =
        combine(_banner, messages) { banner, rows -> chatBannerMessage(banner, rows.isEmpty()) }
            .distinctUntilChanged()
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), null)

    /**
     * False until the first read of this conversation has settled, either way.
     *
     * [messages] starts at `emptyList()` -- `stateIn` has to be given something before the cache
     * query answers -- and an empty timeline is what the screen renders "No messages yet. / Say
     * hello." for. So without this every conversation, including one with a thousand messages,
     * opens by ASSERTING that it is empty and then contradicting itself a frame later. It is the
     * same wrong-answer-shown-confidently case the chat list and the search results already guard
     * against, and it is the one surface that was still making the claim.
     *
     * Set in `finally`, so a conversation that fails to load stops claiming to be empty as well:
     * the error banner is then the thing on screen, which is the truth. The cached timeline is
     * unaffected either way -- it renders as soon as the query answers, whether or not the network
     * read has come back.
     */
    private val _loadedOnce = MutableStateFlow(false)
    val loadedOnce: StateFlow<Boolean> = _loadedOnce.asStateFlow()

    /**
     * The outcome of a SEND, kept apart from [_banner] for the reason [_pinNotice] is.
     *
     * [_banner] describes the CONVERSATION -- a history read that failed -- and is cleared by the
     * next successful refresh. A send that failed is neither: the message is still sitting there
     * marked FAILED, and a refresh that succeeds a second later must not quietly erase the only
     * sentence explaining why it never went. Before this existed `send` was
     * `runCatching { ... }` with the Result discarded, so a failed send said NOTHING at all --
     * SPS §19.5 and SDS §25.1's "error state should explain what happened", both unmet.
     *
     * IT HAS A LIFECYCLE, and [drainOutbox] is the half that was missing. Set by [send] and
     * [retryMessage] and cleared by the next send, retry or dismissal, it survived across a
     * conversation being left and re-entered -- so the outbox pass that runs on every open either
     * carried a message out successfully under a stale "No connection" banner, or failed again and
     * said nothing at all. Both are the same defect the KDoc on [send] records as fixed, on the
     * other path.
     */
    private val _sendNotice = MutableStateFlow<UiText?>(null)
    val sendNotice: StateFlow<UiText?> = _sendNotice.asStateFlow()

    /** True while a manual refresh is in flight, for SDS §19.4's pull-to-refresh. */
    private val _refreshing = MutableStateFlow(false)
    val refreshing: StateFlow<Boolean> = _refreshing.asStateFlow()

    /** True while a page of older history is being fetched, so the trigger cannot re-enter. */
    private val _loadingOlder = MutableStateFlow(false)
    val loadingOlder: StateFlow<Boolean> = _loadingOlder.asStateFlow()

    private val _attachmentInFlight = MutableStateFlow(false)
    val attachmentInFlight: StateFlow<Boolean> = _attachmentInFlight.asStateFlow()

    /**
     * The conversation's pinned messages, newest pin first, capped at five by the contract.
     *
     * NOT part of [messages] and not in the cache. `listPinnedMessages` is its own endpoint over its
     * own table, a pin is per-CONVERSATION rather than a property of the message row, and the cache
     * has nowhere to put it -- so this is fetched on open and re-fetched when the socket says a pin
     * changed. See `MessageRepository.pinnedMessages`.
     *
     * Empty is the honest initial value here, unlike the timeline: an empty pin list renders as no
     * banner at all rather than as a claim, so there is nothing for a `loadedOnce` guard to protect
     * against.
     */
    private val _pinned = MutableStateFlow<List<Message>>(emptyList())
    val pinned: StateFlow<List<Message>> = _pinned.asStateFlow()

    /**
     * The outcome of the last pin action -- the five-per-conversation limit, or a failure.
     *
     * Separate from [_banner], which describes the CONVERSATION. A refused pin must not make the
     * timeline look broken, and a refresh that succeeds afterwards must not silently erase the
     * explanation for why the pin did not happen. Exactly the split `ChatListViewModel` keeps
     * between its `error` and its `notice`.
     */
    private val _pinNotice = MutableStateFlow<UiText?>(null)
    val pinNotice: StateFlow<UiText?> = _pinNotice.asStateFlow()

    /** 0f..1f for the PUT to object storage, which is the only part that takes measurable time. */
    private val _attachmentProgress = MutableStateFlow(0f)
    val attachmentProgress: StateFlow<Float> = _attachmentProgress.asStateFlow()

    private val _composerContext = MutableStateFlow<ComposerContext>(ComposerContext.None)
    val composerContext: StateFlow<ComposerContext> = _composerContext.asStateFlow()

    private val _typing = MutableStateFlow(TypingState.Empty)

    /**
     * The user ids the socket says are typing, already expired.
     *
     * The ticker runs ONLY while somebody is actually typing, and stops the moment the list
     * empties -- a plain repeating timer would keep the view model awake for the whole time a
     * conversation is open, to answer a question whose answer is almost always "nobody".
     *
     * Re-reading on a timer is not optional: [TypingState] expires entries by elapsed time rather
     * than sweeping them, so nothing pushes a change when the last signal goes stale. Without
     * this the indicator would stay on screen until the next unrelated frame arrived.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    private val typingUserIds: Flow<List<String>> = _typing
        .flatMapLatest { state ->
            flow {
                while (true) {
                    val ids = state.typistsAt(nowMs())
                    emit(ids)
                    // Nothing left that can expire, so there is nothing to wake up for. A new
                    // signal replaces `state` and restarts this.
                    if (ids.isEmpty()) break
                    delay(TYPING_TICK_MS)
                }
            }
        }
        .distinctUntilChanged()

    /**
     * Who the screen should say is typing, as display names.
     *
     * Names are resolved LATE, against whatever member list the conversation has right now, which is
     * what lets a frame that beat the REST read survive until the names land -- see
     * [resolveTypistNames] for the whole reason. Recombining on every [chat] emission is what makes
     * "late" work: nothing has to re-deliver the frame.
     */
    val typingNames: StateFlow<List<String>> =
        combine(typingUserIds, chat) { ids, conversation -> resolveTypistNames(ids, conversation) }
            .distinctUntilChanged()
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyList())

    init {
        // Reconciliation BEFORE the history read, and it is not an ordering preference.
        // `listMessages` excludes deleted rows, so a refresh can only ever add and amend -- it
        // has no way to say that something cached is gone. The change feed is the only thing that
        // does. Running it first means a message deleted while this device was away is gone from
        // the timeline on the frame the conversation opens, rather than a moment later.
        reconcile()
        refresh()
        // A message composed before the app was killed goes out now, reusing its original
        // identifiers so the server collapses it rather than creating a duplicate.
        drainOutbox()
        // A no-op unless this conversation is missing from the live socket's channel claim, which
        // is the case for any chat that did not exist when the token was signed. Typing has no
        // second route -- it is published to that channel and stored nowhere -- so an unsubscribed
        // conversation shows no indicator at all, however healthy the socket looks.
        realtime.ensureSubscribed(chatId.value)
        refreshPinned()
        observeRealtime()
        observePins()
        observeTyping()
        observeReading()
        observeReadReceipts()
        loadSquadStreak()
    }

    private fun loadSquadStreak() {
        val repo = squadStreaks ?: return
        viewModelScope.launch {
            _squadStreak.value = repo.getStreak(chatId.value)
        }
    }

    fun startSquadStreak() {
        val repo = squadStreaks ?: return
        if (_startStreakInProgress.value) return
        _startStreakInProgress.value = true
        viewModelScope.launch {
            try {
                _squadStreak.value = repo.startStreak(chatId.value)
            } catch (_: Throwable) {
                runCatching {
                    _squadStreak.value = repo.getStreak(chatId.value)
                }
            } finally {
                _startStreakInProgress.value = false
            }
        }
    }

    fun nudgeSquadStreak() {
        val repo = squadStreaks ?: return
        if (_nudgeInProgress.value) return
        _nudgeInProgress.value = true
        viewModelScope.launch {
            try {
                repo.nudge(chatId.value)
                delay(2000)
            } finally {
                _nudgeInProgress.value = false
            }
        }
    }

    // -------------------------------------------------------------------------- pinned messages

    /**
     * Re-reads the pinned list.
     *
     * Failures are SWALLOWED, and that is the difference between this and [refresh]. The banner is
     * an accessory to a conversation the user is reading; a red bar over their messages because a
     * secondary request failed would be the more visible defect, and whatever was already fetched
     * stays on screen. A pin action that fails is reported -- see [setPinned] -- because that one
     * the user asked for.
     */
    private fun refreshPinned() {
        viewModelScope.launch {
            runCatching { repository.pinnedMessages(chatId) }
                .onSuccess { _pinned.value = it }
        }
    }

    /** Keeps the pinned banner current, from the socket. See [affectsPinnedList] for the rule. */
    private fun observePins() {
        viewModelScope.launch {
            realtime.signals
                .filter { affectsPinnedList(it, chatId) }
                // collectLatest, so pinning three messages in a row costs ONE re-read rather than
                // three, exactly as it does for the timeline.
                .collectLatest {
                    delay(REFRESH_DEBOUNCE_MS)
                    refreshPinned()
                }
        }
    }

    /**
     * Pins or unpins one message. SPS §10.13 and SDS §12.5 both put this in the long-press menu.
     *
     * The list is re-read from the server afterwards rather than edited locally: the cap is
     * enforced there, the ORDER is `pinned_at DESC` and only the server knows it, and a locally
     * inserted row would have to guess both. A pin costs one round trip and a tap the user made.
     *
     * THE 409 IS NOT A FAILURE, it is the limit. `pin_limit_reached` is shared with the pinned
     * CONVERSATIONS cap of `ChatListViewModel`, so the sentence has to be chosen here -- the server's
     * own message would be the right one by accident today and the wrong one the moment either cap
     * moves.
     */
    fun setPinned(messageId: MessageId, pinned: Boolean) {
        _pinNotice.value = null
        viewModelScope.launch {
            try {
                if (pinned) repository.pinMessage(chatId, messageId) else repository.unpinMessage(chatId, messageId)
                _pinned.value = repository.pinnedMessages(chatId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: Throwable) {
                _pinNotice.value = when {
                    // A PLURAL OVER A CONSTANT. The cap is five today and the sentence names it,
                    // so Russian has to agree with a number that can move: `5 закреплённых
                    // сообщений` and `2 закреплённых сообщения` are different words for the same
                    // fact, and `if (n == 1)` cannot produce either.
                    error is ApiError.Conflict -> Res.plurals.chat_pin_limit.count(MAX_PINNED_MESSAGES)
                    error is ApiError.Offline -> Res.string.chat_offline.text()
                    error is ApiError.Timeout -> Res.string.chat_timeout.text()
                    pinned -> Res.string.chat_pin_failed.text()
                    else -> Res.string.chat_unpin_failed.text()
                }
            }
        }
    }

    fun dismissPinNotice() {
        _pinNotice.value = null
    }

    /**
     * Folds incoming read receipts into [_peerReadUpTo], and does NOTHING else -- no refetch, no
     * cache write. That is the entire point: a tick turning is not worth reloading a conversation
     * for, and before this existed a receipt only became visible on the next full refresh, which in
     * a conversation the user was sitting in meant leaving it and coming back.
     *
     * THE MARK NEVER MOVES BACKWARDS. Two receipts can reach Centrifugo out of order -- they are two
     * independent publishes, and nothing in the socket preserves the order of the reads that caused
     * them -- and a watermark that regressed would turn a pair of ticks back into one in front of
     * somebody watching. `maxOf` costs nothing and makes the question moot; it also absorbs the one
     * case where the server's own timestamps could disagree with its sequence numbers, a clock
     * stepping backwards between two sends.
     *
     * Whose receipt it is is checked by [acceptsReadFrame], and whether it may be BELIEVED for this
     * conversation is decided later, at fold time, by [soleCounterpart] -- deliberately not here.
     * The socket can deliver a receipt before the member list has loaded, and refusing it at arrival
     * would drop it for good for want of a fact that lands a moment later.
     */
    private fun observeReadReceipts() {
        viewModelScope.launch {
            realtime.signals
                .filterIsInstance<RealtimeSignal.Read>()
                .filter { acceptsReadFrame(it, chatId, me) }
                .collect { signal ->
                    _peerReadUpTo.value = _peerReadUpTo.value?.let { maxOf(it, signal.readUpTo) }
                        ?: signal.readUpTo
                }
        }
    }

    /**
     * Marks the conversation read -- the ONE piece of this screen's behaviour that used to live in
     * the navigation graph, where a routing table was deciding when a conversation had been read.
     *
     * THE RULE, stated once: a conversation is read when its newest incoming message has actually
     * been ON SCREEN for [READ_DWELL_MS], not when the route was entered. `ChatScreen` opens pinned
     * to the bottom of the timeline and follows it, so "on screen" and "newest" are the same row --
     * what the dwell adds is that the row has to still be there a moment later. Opening a chat with
     * a long unread run and immediately backing out therefore marks NOTHING: this view model is
     * scoped to the conversation's back-stack entry, so popping it cancels [viewModelScope] and the
     * pending post with it.
     *
     * Failures are swallowed. This is bookkeeping the user did not ask for, and a banner over a
     * conversation they are reading would be the more visible defect.
     *
     * LIMITS, stated honestly rather than left to be discovered. A reader who scrolls far back up is
     * still treated as looking at the newest message, because the screen reports no scroll position
     * to this view model -- closing that gap means a `ChatScreen` parameter fed from its
     * `LazyListState`, see the note on [readReceiptTargets]. And backgrounding the app is not backing
     * out: the view model is not cleared, so a dwell already under way still completes.
     *
     * Collecting [messages] here makes this view model a permanent subscriber, so the timeline query
     * stays warm for as long as the conversation is on the back stack rather than for the five
     * seconds `WhileSubscribed` allows. That is the cheaper of the two options: the alternative is a
     * second SQL query over the same rows.
     */
    private fun observeReading() {
        viewModelScope.launch {
            readReceiptTargets(messages, me).collect { messageId ->
                runCatching { repository.markRead(chatId, messageId) }
            }
        }
    }

    /**
     * The socket says only THAT something changed; REST says what. That split is the contract's
     * (DECISIONS §11), and it is also why a [RealtimeSignal.Resynchronize] refreshes too -- a
     * reconnect means whatever happened while the socket was down was never delivered at all.
     */
    private fun observeRealtime() {
        viewModelScope.launch {
            realtime.signals
                .filter { signal ->
                    when (signal) {
                        is RealtimeSignal.Resynchronize -> true
                        is RealtimeSignal.Event -> signal.event.conversationId == chatId.value
                        // Ephemeral, and there is nothing on the server to reconcile against.
                        // Refreshing here would mean a full message reload per keystroke burst,
                        // which is what a separate signal type exists to prevent.
                        is RealtimeSignal.Typing -> false
                        // THE POINT OF THE FEATURE. There IS something to reconcile against here
                        // -- `Message.status` -- and refreshing would even be correct. It is
                        // refused anyway: reloading a conversation's whole timeline because one
                        // tick turned is the cost that made read receipts a leave-and-come-back
                        // affair in the first place. The frame carries the watermark, so
                        // `observeReadReceipts` folds it in place instead.
                        is RealtimeSignal.Read -> false
                    }
                }
                // collectLatest, so a burst -- someone sending five messages at once -- cancels
                // the pending wait and results in ONE read rather than five.
                .collectLatest { signal ->
                    delay(REFRESH_DEBOUNCE_MS)
                    // A RESYNCHRONIZE IS THE ONE CASE REFRESH CANNOT ANSWER. It means the socket
                    // was down, so whatever happened meanwhile was never delivered -- including
                    // deletions, which `listMessages` filters out and can therefore never report.
                    // Reconciling first is what turns "something changed" back into a timeline
                    // that matches the server; see `MessageRepository.reconcile`.
                    if (signal is RealtimeSignal.Resynchronize) reconcile()
                    refresh()
                }
        }
    }

    /**
     * Applies edits and tombstones the socket did not deliver. Failures are SWALLOWED.
     *
     * A conversation that renders is worth more than a banner about a background reconciliation,
     * and every path that calls this also calls [refresh], which does report. The cost of a quiet
     * failure here is that a deleted message stays visible until the next open -- which is what
     * happened on every open before this was wired, so it is strictly better rather than a new
     * risk.
     */
    private fun reconcile() {
        viewModelScope.launch { runCatching { repository.reconcile(chatId) } }
    }

    /**
     * Folds incoming typing frames into [_typing].
     *
     * What gets through is [acceptsTypingFrame]'s decision, kept out here as a pure function so the
     * two frames that must never reach the indicator can be asserted without a socket.
     *
     * Nothing is resolved to a name here; see [typingNames] for why that has to happen later.
     */
    private fun observeTyping() {
        viewModelScope.launch {
            realtime.signals
                .filterIsInstance<RealtimeSignal.Typing>()
                .filter { acceptsTypingFrame(it, chatId, me) }
                .collect { signal ->
                    _typing.value = _typing.value.onTyping(
                        userId = signal.userId,
                        isTyping = signal.isTyping,
                        atMs = nowMs(),
                    )
                }
        }
    }

    /**
     * Called as the user types. Publishes at most one frame per [TYPING_REPUBLISH_MS].
     *
     * Throttled HERE as well as on the server, for a different reason than the server's: this one
     * is about the 60-requests-per-minute budget in DECISIONS §12, which a request per keystroke
     * would exhaust in about a sentence. The server's throttle protects other clients from the
     * frames; this one protects the user's own session from the rate limiter.
     *
     * Failures are swallowed. A typing indicator that does not appear is not worth an error
     * banner over a conversation the user is in the middle of writing in.
     */
    fun onDraftChanged(draft: String) {
        val typing = draft.isNotBlank()
        val now = nowMs()

        if (!typing) {
            // A STOP is never throttled: dropping it strands "…is typing" on the other person's
            // screen until their TTL expires it.
            if (lastTypingSentAtMs == null) return
            lastTypingSentAtMs = null
            publishTyping(false)
            return
        }

        val last = lastTypingSentAtMs
        if (last != null && now - last < TYPING_REPUBLISH_MS) return
        lastTypingSentAtMs = now
        publishTyping(true)
    }

    fun setReplyingTo(message: Message?) {
        _composerContext.value = message?.let(ComposerContext::Reply) ?: ComposerContext.None
    }

    fun setEditingMessage(message: Message?) {
        if (message != null && (!message.canEdit || !message.isMine(me))) return
        _composerContext.value = message?.let(ComposerContext::Edit) ?: ComposerContext.None
    }

    fun editMessage(text: String) {
        val target = (_composerContext.value as? ComposerContext.Edit)?.message ?: return
        val content = text.trim()
        if (content.isEmpty()) return
        _composerContext.value = ComposerContext.None
        if (content == target.text) return

        _sendNotice.value = null
        viewModelScope.launch {
            try {
                repository.edit(target.id, content, target.version)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = when (e) {
                    is ApiError.Forbidden -> Res.string.chat_edit_forbidden.text()
                    is ApiError.Conflict -> Res.string.chat_edit_conflict.text()
                    else -> transportNotice(e) ?: Res.string.chat_edit_failed.text()
                }
            }
        }
    }

    private fun publishTyping(isTyping: Boolean) {
        viewModelScope.launch { runCatching { repository.setTyping(chatId, isTyping) } }
    }

    /**
     * Picks an image, uploads it, and sends it as a message.
     *
     * The three steps fail differently and are reported differently. A cancelled pick is not a
     * failure and says nothing. A rejected image (too large, wrong type) is the user's to fix, so
     * it names the reason. Anything else is ours.
     *
     * Guarded by [_attachmentInFlight] rather than by the button alone: the screen disables the
     * control, but a second call arriving from a recomposition or a fast double tap must not
     * start a second upload sharing the first one's progress.
     */
    fun attachImage() {
        // Read here, on the tap, so it is the CURRENT Activity's chooser -- see [images].
        val picker = images() ?: return
        val uploader = uploads ?: return
        if (_attachmentInFlight.value) return

        _attachmentInFlight.value = true
        viewModelScope.launch {
            try {
                val picked = picker.pick(UploadPurpose.CHAT_IMAGE) ?: return@launch
                val media = uploader.upload(picked, UploadPurpose.CHAT_IMAGE) { fraction ->
                    _attachmentProgress.value = fraction
                }
                repository.sendImage(chatId, media.id, replyingTo = replyingMessage())
                _composerContext.value = ComposerContext.None
                _banner.value = null
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                // A FIXED sentence, not a refresh failure: an image that could not be sent says the
                // same thing whether or not the timeline has anything in it, so it must not pick up
                // "this conversation may be out of date" from [chatBannerMessage].
                _banner.value = ChatBanner.Fixed(
                    when {
                        // Thrown by UploadRepository's pre-flight, which states the contract limit
                        // that was broken -- worth showing, because the user can pick another photo.
                        e is IllegalArgumentException -> Res.string.chat_image_rejected.text()
                        e is ApiError.Offline -> Res.string.chat_offline.text()
                        e is ApiError.Timeout -> Res.string.chat_timeout.text()
                        else -> Res.string.chat_image_failed.text()
                    },
                )
            } finally {
                _attachmentInFlight.value = false
                _attachmentProgress.value = 0f
            }
        }
    }

    fun startVoiceRecording() {
        if (voiceComposerMode(_voiceComposer.value) != VoiceComposerMode.Idle || finishingVoice) return
        val recorder = voiceRecorders() ?: return
        pendingVoiceGesture = PendingVoiceGesture.None
        val session = ++voiceSession
        _sendNotice.value = null
        _voiceComposer.value = VoiceComposerState(requestingPermission = true)
        voiceStartJob?.cancel()
        voiceStartJob = viewModelScope.launch {
            val started = try {
                recorder.start()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                if (session == voiceSession) {
                    _voiceComposer.value = VoiceComposerState()
                    _sendNotice.value = Res.string.chat_voice_failed.text()
                }
                return@launch
            }
            if (session != voiceSession) {
                recorder.cancel()
                return@launch
            }
            if (!started) {
                _voiceComposer.value = VoiceComposerState()
                if (recorder.state.value == VoiceCaptureState.PermissionDenied) {
                    _sendNotice.value = Res.string.chat_voice_permission_denied.text()
                } else {
                    _sendNotice.value = Res.string.chat_voice_failed.text()
                }
                return@launch
            }
            when (pendingVoiceGesture) {
                PendingVoiceGesture.Release,
                PendingVoiceGesture.Cancel -> {
                    recorder.cancel()
                    _voiceComposer.value = VoiceComposerState()
                    pendingVoiceGesture = PendingVoiceGesture.None
                    return@launch
                }
                PendingVoiceGesture.Lock -> Unit
                PendingVoiceGesture.None -> Unit
            }
            activeVoiceRecorder = recorder
            _voiceComposer.value = VoiceComposerState(
                active = true,
                locked = pendingVoiceGesture == PendingVoiceGesture.Lock,
            )
            pendingVoiceGesture = PendingVoiceGesture.None
            voiceCaptureJob?.cancel()
            voiceCaptureJob = viewModelScope.launch {
                recorder.state.collectLatest { capture ->
                    when (capture) {
                        is VoiceCaptureState.Active -> {
                            val previous = _voiceComposer.value
                            _voiceComposer.value = previous.copy(
                                active = true,
                                requestingPermission = false,
                                elapsedMs = capture.elapsedMs,
                                amplitude = capture.amplitude,
                                waveform = if (capture.paused) {
                                    previous.waveform
                                } else {
                                    appendLiveWaveform(previous.waveform, capture.amplitude)
                                },
                                paused = capture.paused,
                            )
                            if (capture.elapsedMs >= MAX_VOICE_DURATION_MS && !finishingVoice) {
                                finishVoiceRecording()
                            }
                        }
                        VoiceCaptureState.Failed -> {
                            _voiceComposer.value = VoiceComposerState()
                            _sendNotice.value = Res.string.chat_voice_failed.text()
                        }
                        else -> Unit
                    }
                }
            }
        }
    }

    /** Finger released: send immediately unless the upward lock gesture claimed the recording. */
    fun releaseVoiceRecording() {
        if (_voiceComposer.value.requestingPermission) {
            // Releasing the finger is part of a completed upward lock gesture. Do not overwrite
            // that stronger intent while the system permission sheet is still resolving.
            if (pendingVoiceGesture != PendingVoiceGesture.Lock) {
                pendingVoiceGesture = PendingVoiceGesture.Release
            }
            return
        }
        if (!_voiceComposer.value.locked) finishVoiceRecording()
    }

    fun lockVoiceRecording() {
        if (_voiceComposer.value.requestingPermission) {
            pendingVoiceGesture = PendingVoiceGesture.Lock
        } else if (_voiceComposer.value.active) {
            _voiceComposer.value = _voiceComposer.value.copy(locked = true)
        }
    }

    fun cancelVoiceRecording() {
        pendingVoiceGesture = PendingVoiceGesture.Cancel
        voiceSession += 1
        voiceStartJob?.cancel()
        voiceStartJob = null
        voiceCaptureJob?.cancel()
        voiceCaptureJob = null
        voiceFinishJob?.cancel()
        voiceFinishJob = null
        activeVoiceRecorder?.cancel()
        activeVoiceRecorder = null
        finishingVoice = false
        val uploadedId = pendingVoiceMediaId
        pendingVoice = null
        pendingVoiceMediaId = null
        pendingVoiceReply = null
        pendingVoiceClientId = null
        pendingVoiceIdempotencyKey = null
        _voiceComposer.value = VoiceComposerState()
        if (uploadedId != null) {
            viewModelScope.launch { runCatching { uploads?.discard(uploadedId) } }
        }
    }

    fun toggleVoicePause() {
        val recorder = activeVoiceRecorder ?: return
        if (_voiceComposer.value.paused) recorder.resume() else recorder.pause()
    }

    fun finishVoiceRecording() {
        val recorder = activeVoiceRecorder
        if (recorder == null && pendingVoice == null) return
        if (finishingVoice) return
        finishingVoice = true
        voiceCaptureJob?.cancel()
        val session = voiceSession
        voiceFinishJob?.cancel()
        voiceFinishJob = viewModelScope.launch {
            try {
                val voice = pendingVoice ?: recorder?.stop()
                if (session != voiceSession) return@launch
                if (recorder != null) activeVoiceRecorder = null
                if (voice == null) {
                    _voiceComposer.value = VoiceComposerState()
                    _sendNotice.value = Res.string.chat_voice_too_short.text()
                    return@launch
                }
                if (pendingVoice == null) {
                    pendingVoice = voice
                    pendingVoiceReply = replyingMessage()
                    pendingVoiceClientId = newUuid()
                    pendingVoiceIdempotencyKey = newUuid()
                }
                val uploader = uploads ?: return@launch
                _voiceComposer.value = VoiceComposerState(
                    uploading = true,
                    elapsedMs = voice.durationMs,
                    amplitude = voice.waveform.lastOrNull() ?: 0,
                    waveform = voice.waveform,
                )
                val mediaId = pendingVoiceMediaId ?: uploader.uploadVoice(voice) { fraction ->
                    if (session == voiceSession) _attachmentProgress.value = fraction
                }.id.also { pendingVoiceMediaId = it }
                if (session != voiceSession) {
                    runCatching { uploader.discard(mediaId) }
                    return@launch
                }
                repository.sendVoice(
                    chatId = chatId,
                    mediaId = mediaId,
                    replyingTo = pendingVoiceReply,
                    clientId = requireNotNull(pendingVoiceClientId),
                    idempotencyKey = requireNotNull(pendingVoiceIdempotencyKey),
                )
                if (session != voiceSession) return@launch
                _composerContext.value = ComposerContext.None
                pendingVoice = null
                pendingVoiceMediaId = null
                pendingVoiceReply = null
                pendingVoiceClientId = null
                pendingVoiceIdempotencyKey = null
                _voiceComposer.value = VoiceComposerState()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                if (session != voiceSession) return@launch
                recorder?.cancel()
                activeVoiceRecorder = null
                val voice = pendingVoice
                _voiceComposer.value = if (voice == null) VoiceComposerState() else VoiceComposerState(
                    active = true,
                    locked = true,
                    paused = true,
                    elapsedMs = voice.durationMs,
                    amplitude = voice.waveform.lastOrNull() ?: 0,
                    waveform = voice.waveform,
                    readyToRetry = true,
                )
                _sendNotice.value = Res.string.chat_voice_failed.text()
            } finally {
                finishingVoice = false
                _attachmentProgress.value = 0f
            }
        }
    }

    fun toggleVoicePlayback(message: Message) {
        val url = message.voiceUrl ?: return
        voicePlayer?.toggle(message.clientId, url, message.voiceDurationMs ?: 0L)
    }

    fun seekVoicePlayback(message: Message, positionMs: Long) {
        voicePlayer?.seekTo(message.clientId, positionMs)
    }

    fun cycleVoicePlaybackSpeed(message: Message) {
        voicePlayer?.cycleSpeed(message.clientId)
    }

    /**
     * Sends, and SAYS SO WHEN IT FAILS.
     *
     * The row is already on screen as SENDING by the time this returns or throws, and a failure
     * marks it FAILED rather than removing what the user typed -- but until now that was the only
     * signal: a small red glyph the same size as a tick, which SDS §25.1 ("error state should
     * explain what happened") is not satisfied by. The Result was discarded outright.
     */
    fun send(text: String) {
        val parent = replyingMessage()
        _composerContext.value = ComposerContext.None
        _sendNotice.value = null
        viewModelScope.launch {
            try {
                repository.send(chatId, me, text, replyingTo = parent)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = sendFailureNotice(e)
            }
        }
        // The draft is cleared by the composer on send, which is a stop in every sense except
        // the one the composer reports -- it raises no change event for its own reset.
        if (lastTypingSentAtMs != null) {
            lastTypingSentAtMs = null
            publishTyping(false)
        }
    }

    private fun replyingMessage(): Message? =
        (_composerContext.value as? ComposerContext.Reply)?.message

    fun dismissSendNotice() {
        _sendNotice.value = null
    }

    /**
     * The outbox pass that runs on entering a conversation, WITH ITS RESULT READ.
     *
     * It used to be `runCatching { repository.retryPending() }` with the Result discarded outright
     * -- which is precisely the defect [send]'s KDoc records as fixed, reappearing on the retry
     * path. The consequence was a notice with no lifecycle: re-entering a conversation either sent
     * the pending message successfully while the previous failure's sentence was still on screen,
     * or failed again and said nothing.
     *
     * `retryPending` does not throw for a message that would not go -- [PendingPassReport] is how
     * it says so -- so the outcome is READ rather than caught. The `try` is for the pass itself
     * (the cache read that feeds it), and [CancellationException] is rethrown before anything else
     * because a cancelled pass is the screen closing, not a message failing.
     *
     * WHAT IT CANNOT SAY, stated rather than left to be discovered: `selectPending` has no
     * conversation filter, so a message stranded in ANOTHER chat is reported by the banner in this
     * one. That is why [outboxDrainNotice]'s sentence names no conversation. Narrowing it means a
     * per-conversation drain on `MessageRepository`, which this change does not own.
     */
    private fun drainOutbox() {
        viewModelScope.launch {
            try {
                _sendNotice.value = outboxDrainNotice(repository.retryPending(), _sendNotice.value)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = sendFailureNotice(e)
            }
        }
    }

    // ------------------------------------------------------------------------ message actions

    /**
     * SPS §19.5 and STA §4.8, both one line and both unmet until now: "Failed messages should
     * show a retry action" / "remain visible with retry action".
     *
     * The row keeps its original `client_id` and `Idempotency-Key`, so pressing this twice cannot
     * post the message twice -- the server collapses the repeat. Reported through [_sendNotice]
     * for the same reason a first send is.
     */
    fun retryMessage(clientId: String) {
        _sendNotice.value = null
        viewModelScope.launch {
            try {
                repository.retry(chatId, clientId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = sendFailureNotice(e)
            }
        }
    }

    /**
     * Abandons a message that never left the device.
     *
     * The other half of what SPS §19.4's `[ Retry ]` implies: a user who has decided a message is
     * not going out needs a way to stop looking at it, and without one the row sits in the outbox
     * costing a request on every chat open until its retry bound expires.
     */
    fun discardMessage(clientId: String) {
        viewModelScope.launch { runCatching { repository.discard(chatId, clientId) } }
    }

    /**
     * `deleteMessage`. SDS §12.5 and SPS §10.13 both list Delete; neither existed on this client.
     *
     * The two scopes are separate decisions the user makes, not a fallback ladder -- see
     * `MessageActionsDialog`, which offers "Delete for everyone" only when the server said it
     * would be accepted.
     */
    fun deleteMessage(messageId: MessageId, forEveryone: Boolean) {
        _sendNotice.value = null
        viewModelScope.launch {
            try {
                repository.delete(
                    messageId,
                    if (forEveryone) MessageDeleteScope.EVERYONE else MessageDeleteScope.SELF,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = when {
                    // The 48-hour window, which is a fact about the message rather than a fault.
                    e is ApiError.Conflict -> Res.string.chat_delete_too_old.text()
                    e is ApiError.Forbidden -> Res.string.chat_delete_forbidden.text()
                    else -> transportNotice(e) ?: Res.string.chat_delete_failed.text()
                }
            }
        }
    }

    /** Copies selected server messages into another conversation in their visible order. */
    fun forwardMessages(messageIds: List<MessageId>, targetChatId: ChatId) {
        if (messageIds.isEmpty()) return
        _sendNotice.value = null
        viewModelScope.launch {
            try {
                repository.forward(messageIds, targetChatId)
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (e: Throwable) {
                _sendNotice.value = transportNotice(e) ?: Res.string.chat_forward_failed.text()
            }
        }
    }

    fun react(messageId: MessageId, emoji: String) {
        _sendNotice.value = null
        val lock = reactionLocks.getOrPut(messageId.value to emoji) { Mutex() }
        viewModelScope.launch {
            lock.withLock {
                try {
                    repository.react(messageId, emoji)
                } catch (cancellation: CancellationException) {
                    throw cancellation
                } catch (_: Throwable) {
                    // The picker closes on selection, so a refusal needs a visible, localized result.
                    _sendNotice.value = Res.string.chat_reaction_failed.text()
                }
            }
        }
    }

    // --------------------------------------------------------------------------- older history

    /**
     * One page further back, cache first.
     *
     * THE CACHE IS TRIED BEFORE THE NETWORK, which is what makes paging back through history the
     * app already holds instant and free: the cache query is `LIMIT ?`, so most of the time there
     * is nothing to fetch and the only thing missing was a larger window. Only when the cache has
     * nothing older does this spend a request.
     *
     * The cursor is adopted from the first history read ONLY. A background refresh always fetches
     * the newest page, and letting its cursor overwrite this one would walk the reader back to
     * the top and stall there, re-fetching a page the cache already holds forever.
     */
    fun loadOlder() {
        if (_loadingOlder.value || historyExhausted) return
        val oldest = messages.value.lastOrNull()?.sentAt ?: return

        _loadingOlder.value = true
        viewModelScope.launch {
            try {
                if (repository.hasCachedOlder(chatId, oldest)) {
                    _window.value += MessageRepository.PAGE_SIZE.toLong()
                    return@launch
                }
                val cursor = historyCursor
                if (cursor == null) {
                    historyExhausted = true
                    return@launch
                }
                val next = repository.loadOlder(chatId, cursor)
                historyCursor = next
                if (next == null) historyExhausted = true
                _window.value += MessageRepository.PAGE_SIZE.toLong()
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                // Silent, and deliberately so: the reader still has everything they had, and a
                // banner over a conversation because the page BEFORE it could not be fetched
                // would be reporting a failure they did not ask for. The next scroll retries.
            } finally {
                _loadingOlder.value = false
            }
        }
    }

    fun refresh() {
        _refreshing.value = true
        viewModelScope.launch {
            try {
                val cursor = repository.refresh(chatId)
                // Only before the reader has paged back at all; see [loadOlder].
                if (historyCursor == null && !historyExhausted) historyCursor = cursor
                _banner.value = null
            } catch (cancellation: CancellationException) {
                // Rethrown before anything else, as every other handler in this file does. A
                // cancelled refresh is the reader leaving the conversation, not a failure, and
                // catching it below painted the banner with a JobCancellationException's toString.
                throw cancellation
            } catch (e: Throwable) {
                // Catches Throwable, not just ApiError: a decode failure is a
                // SerializationException, and catching only ApiError let it vanish -- the
                // request succeeded, nothing was written, and nothing was reported.
                // The cached timeline stays on screen; only a banner appears -- and WHAT IT SAYS
                // is decided later, against that timeline. See [ChatBanner].
                _banner.value = refreshFailure(e)
            } finally {
                _loadedOnce.value = true
                _refreshing.value = false
            }
        }
    }

    /** When we last told the server this user is typing. Null means we have said they stopped. */
    private var lastTypingSentAtMs: Long? = null

    private fun nowMs(): Long = Clock.System.now().toEpochMilliseconds()

    private companion object {
        /** Long enough to collapse a burst, short enough that a single message feels instant. */
        const val REFRESH_DEBOUNCE_MS = 150L

        /**
         * FIVE MESSAGES PER CONVERSATION -- `maxItems: 5` on `listPinnedMessages`, DECISIONS §5
         * ("five messages per conversation") and `MessageService.MAX_PINNED_MESSAGES`.
         *
         * Deliberately NOT the same number as `ChatListViewModel.MAX_PINNED`, which caps pinned
         * CONVERSATIONS per user. The two happen to be five today and are different limits; naming
         * them separately is what keeps one moving from silently rewording the other's message.
         */
        const val MAX_PINNED_MESSAGES = 5

        /**
         * How often [typingNames] re-evaluates while somebody is typing. Well under
         * [TYPING_TTL_MS], so the indicator disappears promptly once the signals stop, and coarse
         * enough that it costs one wake-up per second only for as long as it is on screen.
         */
        const val TYPING_TICK_MS = 1_000L
    }
}

// ------------------------------------------------------------------------------- send failures
//
// What the user is told when a message does not go. Pure, because it is a branch per failure kind
// and the wrong branch is invisible from outside the running app -- the message is on screen
// either way, with a sentence that is either useful or misleading.

/**
 * The transport half, shared by every action on this screen. Null means "not a transport
 * failure", so the caller can say something specific about its own operation instead.
 */
internal fun transportNotice(error: Throwable): UiText? = when (error) {
    is ApiError.Offline -> Res.string.chat_send_offline.text()
    is ApiError.Timeout -> Res.string.chat_send_timeout.text()
    else -> null
}

/**
 * Why a send failed, in a sentence, following SPS §19.4's shape -- what happened, then what to do
 * about it.
 *
 * THE THREE THAT ARE NOT RETRIES ARE THE POINT. A 403 after a block, a validation failure and a
 * suspended account will all fail again identically, however many times the message is re-sent,
 * and telling somebody to try again is worse than telling them nothing: the automatic pass has
 * already tried, and the row is sitting there with a cross on it. `ComposePostViewModel` draws the
 * same distinction for the same reason.
 */
internal fun sendFailureNotice(error: Throwable): UiText {
    transportNotice(error)?.let { return it }
    return when (error) {
        // The message cannot be delivered to this conversation at all -- blocked, removed, or
        // restricted. Retrying is the one thing that will not help.
        is ApiError.Forbidden -> Res.string.chat_send_forbidden.text()
        is ApiError.NotFound -> Res.string.chat_send_not_found.text()
        is ApiError.Validation -> Res.string.chat_send_invalid.text()
        is ApiError.RateLimited -> Res.string.chat_send_rate_limited.text()
        is ApiError.AccountSuspended -> Res.string.chat_send_suspended.text()
        else -> Res.string.chat_send_unknown.text()
    }
}

// ------------------------------------------------------------------------------ the outbox pass

/**
 * What the outbox drain on entering a conversation leaves on screen.
 *
 * THREE OUTCOMES, and the middle one is the reason this is a function rather than an assignment:
 *
 *  * something the pass ATTEMPTED failed -- say so, and replace whatever was there;
 *  * the pass carried everything out and left nothing behind -- clear it, because the sentence
 *    still on screen ("No connection. The message stays here and can be sent again.") is now
 *    describing a message that has since gone;
 *  * the pass left rows it deliberately did not attempt ([isAutoRetryable] refuses a row past
 *    [com.swetdot.swetgram.data.MAX_AUTO_RETRIES] or with no stored idempotency key) -- it has
 *    learned NOTHING about those, so it must neither clear the notice nor invent a new one. The
 *    previous sentence stands.
 *
 * The sentence names no conversation, and that is deliberate: `selectPending` has no chat filter,
 * so a row that failed here may belong to a conversation the user is not looking at. It points at
 * the gesture instead, in the words `failedSendNote` already uses on the row itself.
 */
internal fun outboxDrainNotice(report: PendingPassReport, previous: UiText?): UiText? = when {
    report.failed.isNotEmpty() -> Res.string.chat_outbox_failed.text()
    report.skipped.isNotEmpty() -> previous
    else -> null
}

// ------------------------------------------------------------------- the conversation's banner

/**
 * What the conversation-level banner is ABOUT. The sentence is [chatBannerMessage]'s.
 *
 * The split exists because one of the two cases cannot be worded until the timeline is known. A
 * failed refresh over an EMPTY screen is a failure to load the conversation; the same failure over
 * a cached conversation is a failure to check whether it changed, and saying "Could not load
 * messages" above forty readable messages is a banner contradicting the content under it. Nothing
 * else clears it either -- a cache render is not a server answer -- so it sits there being wrong
 * until the user acts.
 */
internal sealed interface ChatBanner {
    /**
     * A refresh that failed. [detail] carries the diagnostic for the one case that has no sentence
     * of its own -- a decode failure, which is not an [ApiError] at all.
     */
    data class RefreshFailed(val kind: RefreshFailureKind, val detail: String? = null) : ChatBanner

    /**
     * A sentence that is already the whole truth wherever it appears, and must not pick up the
     * freshness clause: an image that could not be sent is not a statement about the conversation.
     */
    data class Fixed(val message: UiText) : ChatBanner
}

/** Why a refresh failed, at the granularity the two sentences need. */
internal enum class RefreshFailureKind {
    OFFLINE,
    TIMEOUT,

    /** The server answered and refused, or answered something we could not use. */
    REFUSED,

    /** Not an [ApiError] at all -- a decode failure is the case this exists for. */
    UNEXPECTED,
}

/** Classifies a refresh failure. Kept apart from the wording so the wording can depend on more. */
internal fun refreshFailure(error: Throwable): ChatBanner.RefreshFailed = when (error) {
    is ApiError.Offline -> ChatBanner.RefreshFailed(RefreshFailureKind.OFFLINE)
    is ApiError.Timeout -> ChatBanner.RefreshFailed(RefreshFailureKind.TIMEOUT)
    is ApiError -> ChatBanner.RefreshFailed(RefreshFailureKind.REFUSED)
    else -> ChatBanner.RefreshFailed(
        RefreshFailureKind.UNEXPECTED,
        detail = error::class.simpleName + ": " + (error.message ?: ""),
    )
}

/**
 * The banner's sentence, or null.
 *
 * [timelineIsEmpty] is the whole reason this is not a `when` at the call site. With nothing on
 * screen the failure IS the conversation, and the wording is unchanged from what shipped. With a
 * cached conversation underneath, the only thing the failure establishes is that it may have moved
 * on without us -- so the sentence says that, and the Retry beside it is then an offer rather than
 * the user's only hope of ever seeing the chat.
 *
 * TWO TABLES RATHER THAN A CAUSE WITH A CLAUSE STUCK ON THE END, and that is the localisation change
 * rather than a tidy-up. This used to build `"${cause.trimEnd(' ', '.')}. $MAY_BE_OUT_OF_DATE"`,
 * which joins two translated fragments with a full stop and a space. It survives in English because
 * every cause happens to be a standalone sentence there; nothing makes that true of another
 * language, no call site can tell when it has stopped being true, and the result would be a banner
 * that reads as two halves of different sentences to the only readers who can see it. So each
 * combination is one key, written whole by whoever writes the language.
 *
 * The UNEXPECTED case is the exception and stays composed, because its first half is not copy at
 * all: it is a class name and an exception message, and it is the same characters in every
 * language. It travels as an ARGUMENT, which is exactly the distinction `UiText` exists to keep.
 */
internal fun chatBannerMessage(banner: ChatBanner?, timelineIsEmpty: Boolean): UiText? =
    when (banner) {
        null -> null
        is ChatBanner.Fixed -> banner.message
        is ChatBanner.RefreshFailed -> {
            // A diagnostic, never a sentence. Trimmed here so the key can supply the full stop:
            // `IllegalStateException: boom.` reads as one thing, `boom.. ` as a typo.
            val detail = banner.detail?.takeIf { it.isNotBlank() }?.trimEnd(' ', '.')
            if (timelineIsEmpty) {
                when (banner.kind) {
                    RefreshFailureKind.OFFLINE -> Res.string.chat_offline.text()
                    RefreshFailureKind.TIMEOUT -> Res.string.chat_timeout.text()
                    RefreshFailureKind.REFUSED -> Res.string.chat_banner_load_refused.text()
                    // Falls back rather than rendering an empty banner: a diagnostic that is
                    // missing is still a refresh that failed, and the user is owed the sentence.
                    RefreshFailureKind.UNEXPECTED -> detail
                        ?.let { Res.string.value_passthrough.text(it) }
                        ?: Res.string.chat_banner_load_refused.text()
                }
            } else {
                when (banner.kind) {
                    RefreshFailureKind.OFFLINE -> Res.string.chat_banner_stale_offline.text()
                    RefreshFailureKind.TIMEOUT -> Res.string.chat_banner_stale_timeout.text()
                    RefreshFailureKind.REFUSED -> Res.string.chat_banner_stale_refused.text()
                    RefreshFailureKind.UNEXPECTED -> detail
                        ?.let { Res.string.chat_banner_stale_detail.text(it) }
                        ?: Res.string.chat_banner_stale_refused.text()
                }
            }
        }
    }

// ------------------------------------------------------------------------------------- typing
//
// The two decisions the typing pipeline makes, lifted out of the flow operators they used to sit
// inside. Neither needs a socket, a clock or a view model to be true, and neither can be observed
// from outside the running app: a frame that is wrongly let through shows the reader somebody
// else's conversation, and a frame that is wrongly dropped shows nothing at all.

/**
 * Whether a `typing` frame belongs to this conversation's indicator.
 *
 * Two kinds are refused, both of which would otherwise be visible defects. Frames for OTHER
 * conversations: the socket carries every channel the user is subscribed to, so a chat left open
 * behind this one delivers into the same stream. And the user's OWN frames: the server publishes to
 * the whole channel including the sender, and "You are typing…" is not a thing to show anybody.
 *
 * Both comparisons ignore case, because the two sides travel by different routes -- one is a UUID
 * rendered by Postgres, the other is what this session was handed at sign-in. A casing difference
 * would not fail loudly; it would quietly show the user their own name typing.
 *
 * `isTyping` is deliberately NOT consulted. A stop frame has to get through exactly as a start does
 * -- it is the frame that clears the indicator, and dropping it would strand "…is typing" on screen
 * until the TTL expired it.
 */
internal fun acceptsTypingFrame(
    signal: RealtimeSignal.Typing,
    chatId: ChatId,
    me: UserId,
): Boolean = signal.conversationId.equals(chatId.value, ignoreCase = true) &&
    !signal.userId.equals(me.value, ignoreCase = true)

/**
 * Turns the ids [TypingState] holds into the names the header shows.
 *
 * Resolved HERE, against the CURRENT member list, rather than when the frame arrived. The socket can
 * deliver a typing frame before the conversation has been read over REST -- opening a chat the cache
 * has never seen is exactly that race -- and resolving on arrival meant such a frame was dropped for
 * good, for want of a name that landed a moment later. Doing it late also means a member who is
 * renamed is renamed in the indicator.
 *
 * Somebody still absent from the member list is SKIPPED rather than shown as a placeholder: a bare
 * id, or "Someone", is worse in the header than no indicator at all. They appear by themselves on
 * the next tick once the conversation loads, because the id is still sitting in [TypingState].
 */
internal fun resolveTypistNames(ids: List<String>, chat: Chat?): List<String> {
    if (ids.isEmpty()) return emptyList()
    val members = chat?.members ?: return emptyList()
    return ids.mapNotNull { id ->
        members.firstOrNull { it.id.value.equals(id, ignoreCase = true) }?.displayName
    }
}

// ---------------------------------------------------------------------------- pinned messages

/**
 * The durable events that can change what the pinned banner should show.
 *
 * `message.pinned` and `message.unpinned` are the obvious two -- a pin is durable, so it travels
 * through the outbox like any other change. The other two are not obvious and are both real:
 *
 *  * `message.deleted`, because `listPinnedMessages` excludes deleted messages. A pinned message
 *    that is deleted silently leaves the list, and a banner still quoting it would be showing text
 *    the server has already withdrawn.
 *  * `message.edited`, because the banner quotes the BODY. An edited pin whose banner is not
 *    re-read keeps the old wording on screen indefinitely.
 */
internal val PIN_RELEVANT_EVENTS: Set<String> = setOf(
    RealtimeEventTypes.MESSAGE_PINNED,
    RealtimeEventTypes.MESSAGE_UNPINNED,
    RealtimeEventTypes.MESSAGE_DELETED,
    RealtimeEventTypes.MESSAGE_EDITED,
)

/**
 * Whether a frame means the pinned list has to be re-read.
 *
 * Pulled out of the flow operator for the reason [acceptsTypingFrame] and [acceptsReadFrame] were:
 * it is a decision about a conversation rather than about a socket, and getting it wrong is silent
 * either way -- too permissive costs requests nobody sees, too strict leaves a stale banner quoting
 * a message that has been unpinned, edited or deleted.
 *
 * THE ONE DELIBERATE OMISSION IS `message.new`, and it is the frequent one. A message that has just
 * arrived cannot already be pinned, and the pin that may follow carries its own event. Refreshing on
 * every arrival would double this screen's request rate in an active conversation -- against
 * DECISIONS §12's 60-per-minute budget -- to re-read a list that cannot have changed.
 *
 * [RealtimeSignal.Resynchronize] always counts, for the reason it always does: a reconnect means
 * whatever happened while the socket was down was never delivered at all. Typing and read receipts
 * never do: neither is durable and neither can move a pin.
 */
internal fun affectsPinnedList(signal: RealtimeSignal, chatId: ChatId): Boolean = when (signal) {
    is RealtimeSignal.Resynchronize -> true
    is RealtimeSignal.Event ->
        signal.event.conversationId.equals(chatId.value, ignoreCase = true) &&
            signal.event.type in PIN_RELEVANT_EVENTS
    is RealtimeSignal.Typing, is RealtimeSignal.Read -> false
}

// ------------------------------------------------------------------------------ read receipts

/**
 * How long the newest message has to stay on screen before the conversation counts as read.
 *
 * Long enough that opening a conversation and immediately leaving it does not clear an unread run
 * the user never looked at, short enough that anybody who actually read the message has posted the
 * receipt before they can reach the back gesture. It is not a debounce for the network -- a burst of
 * arrivals is collapsed by [readReceiptTargets] restarting rather than by this number.
 */
internal const val READ_DWELL_MS: Long = 1_500L

/**
 * Whether a read receipt belongs to this conversation's timeline.
 *
 * The same two refusals [acceptsTypingFrame] makes, for the same two reasons. Frames for OTHER
 * conversations, because the socket carries every channel this session is subscribed to. And the
 * user's OWN, because the server publishes to the whole channel including the reader -- and folding
 * your own watermark in would turn YOUR ticks read the moment you read someone else's message,
 * which is a double tick that means nothing at all.
 *
 * Case-insensitive for the reason the typing filter is: one id is a UUID rendered by Postgres, the
 * other is what this session was handed at sign-in, and a casing difference would not fail loudly.
 * Here it would silently mark the user's own messages read from their own reading.
 */
internal fun acceptsReadFrame(
    signal: RealtimeSignal.Read,
    chatId: ChatId,
    me: UserId,
): Boolean = signal.conversationId.equals(chatId.value, ignoreCase = true) &&
    !signal.userId.equals(me.value, ignoreCase = true)

/**
 * The one other participant whose read watermark IS this conversation's, or null.
 *
 * WHY THIS GATE EXISTS. A receipt says what ONE reader has reached. In a direct chat that is the
 * whole answer, because there is exactly one other person. With more, it is not: the server renders
 * `Message.status` from `MIN(last_read_seq)` over the other members (`MessageRepository
 * .peerLastReadSeq`), so one member reading is not the conversation being read. Folding a single
 * reader's frame there would show two blue ticks for a message most of the room has not seen -- the
 * kind of invented state that is worse than the missing tick this feature set out to fix. So where
 * a sole counterpart cannot be established, nothing is folded and REST stays the only source.
 *
 * Null also for a conversation whose member list has not loaded yet, and for one the signed-in user
 * is not in at all -- the same foreign-row guard `Chat.peer` makes, and for the same reason: "not
 * me" only identifies "them" when I am actually in the room.
 */
internal fun soleCounterpart(chat: Chat?, me: UserId): UserId? {
    val members = chat?.members ?: return null
    if (members.none { it.id == me }) return null
    return members.filter { it.id != me }.singleOrNull()?.id
}

/**
 * The timeline with a live read watermark applied: every own message the counterpart has reached
 * shows as read.
 *
 * A WATERMARK, NOT A RECEIPT PER MESSAGE. `last_read_seq` is cumulative, so one frame settles the
 * whole run behind it -- which is also why nothing here needs to remember individual message ids.
 * The comparison is by [Message.sentAt] because that is the ordering the client has: the contract's
 * `Message` carries no sequence number, and `created_at` is non-decreasing in `seq` since every send
 * holds the conversation counter lock from allocation to commit. See `ReadReceiptEvent`.
 *
 * ONLY [MessageState.SENT] IS PROMOTED, and the exclusions are the point rather than caution:
 *
 *  * SENDING is an optimistic row whose `sentAt` is the LOCAL clock at the moment the user hit
 *    send, not a server timestamp. Enough clock skew and it would sort before a server watermark,
 *    and a message that has not reached the server at all would show as read by somebody.
 *  * FAILED must never be promoted for the same reason, and READ is already there. States move
 *    forward only -- the ladder in `Timeline.mergeWith` says so -- and this function never walks one
 *    back, so a refresh that lands afterwards cannot make a tick flicker.
 *  * Incoming messages are untouched. `status` is meaningful only on the caller's own messages;
 *    that is where ticks are drawn.
 *
 * Returns the SAME list instance when nothing changed, so a receipt that says nothing new -- one for
 * a run already read, or the usual case where REST got there first -- costs no recomposition.
 */
internal fun applyReadWatermark(
    messages: List<Message>,
    me: UserId,
    readUpTo: Instant?,
    counterpart: UserId?,
): List<Message> {
    if (readUpTo == null || counterpart == null || messages.isEmpty()) return messages
    var changed = false
    val folded = messages.map { message ->
        if (message.sender.id != me || message.state != MessageState.SENT) return@map message
        if (message.sentAt > readUpTo) return@map message
        changed = true
        message.copy(state = MessageState.READ)
    }
    return if (changed) folded else messages
}

/**
 * The message id a read receipt should name, or null when there is nothing to mark.
 *
 * Own messages are skipped for two reasons that happen to coincide. A message the user wrote cannot
 * be unread, so naming one would post a request per burst of their own writing; and an own message
 * that has not been acknowledged yet has NO server id at all -- `CacheMappers` falls its `id` back
 * to the client id -- so the server would answer 404 for a message it has never seen. Filtering to
 * incoming messages disposes of both at once, because only an own message can be unacknowledged.
 *
 * The answer is the NEWEST incoming message, which marks the whole run behind it read:
 * `MessageRepository.markRead` marks everything up to the id it is given.
 */
internal fun readReceiptTarget(messagesNewestFirst: List<Message>, me: UserId): String? =
    messagesNewestFirst.firstOrNull { it.sender.id != me }?.id?.value

/**
 * The read receipts a conversation should post, given its timeline over time.
 *
 * The whole rule, as a flow so it can be asserted on a virtual clock instead of by watching a badge:
 *
 *  - only the newest INCOMING message is ever named ([readReceiptTarget]),
 *  - the same one is never posted twice, however often the timeline changes underneath it,
 *  - and it is posted only after it has survived [dwellMs] -- `mapLatest` cancels the wait when a
 *    newer message arrives, so a burst costs ONE receipt for the last of them, and a collector that
 *    is cancelled first (the reader backed out) posts nothing at all.
 *
 * [messages] is the screen's own timeline, so "the newest message" and "what is on screen" are the
 * same row: `ChatScreen` opens pinned to the bottom and follows it. The one case this cannot see is
 * a reader scrolled far back up, which would need the screen to report its `LazyListState` -- an
 * extra `ChatScreen` parameter, and the only piece of the rule still missing.
 */
@OptIn(ExperimentalCoroutinesApi::class)
internal fun readReceiptTargets(
    messages: Flow<List<Message>>,
    me: UserId,
    dwellMs: Long = READ_DWELL_MS,
): Flow<String> = messages
    .map { readReceiptTarget(it, me) }
    .distinctUntilChanged()
    .filterNotNull()
    .mapLatest { target ->
        delay(dwellMs)
        target
    }
